import { apiClient } from '../../core/api';
import { isErrorEnvelope } from '@lib/core/errorEnvelope';

/**
 * [Infra 1.5 #25] The store-manager panel's data plane — recovery cards.
 *
 * One card per failure cause, assembled server-side: the wording (headline / consequence / button
 * labels), the merge (same cause ≥ threshold folds into one card with `items`), and the ORDER all
 * come from the backend's disposition table. The client renders by {@link RecoveryCardShape} ONLY
 * and never branches on classification — `disposition` rides along for debugging, but a client
 * that styles by it will silently disagree with the copy table the day a row moves.
 *
 * Every button press goes through {@link actOnCard} with the card's `token` ("v" + version,
 * opaque). A press made against a card that has since moved is refused with a 409 whose payload IS
 * the current card ({@link CardActionConflict}) — repaint silently, no second round trip.
 */

/** The only thing layout may branch on. merged = N tasks one cause; open_post = fix-in-editor; notice = tell + Delete. */
export type RecoveryCardShape = 'merged' | 'open_post' | 'notice';

export type CardActionKind = 'call' | 'navigate' | 'modal';

export interface CardNavigate {
  route: string;
  params?: Record<string, string> | null;
  back?: boolean;
  /** What to POST once the merchant returns from the route, or null. */
  confirmActionId?: string | null;
}

export interface CardModal {
  /** e.g. `marketing_hub_post_editor` — the panel opens this component in place. */
  component: string;
  params?: Record<string, string> | null;
  confirmActionId?: string | null;
  discardActionId?: string | null;
}

export interface CardAction {
  /** `reconnect_shopify` / `top_up` / `open_post` / `delete` … */
  id: string;
  kind: CardActionKind;
  label: string;
  /** Grey line under the primary button: what pressing it will do. */
  hint?: string | null;
  primary: boolean;
  /** `"v" + version`, opaque. Send back verbatim with the press. */
  token: string;
  navigate?: CardNavigate | null;
  modal?: CardModal | null;
}

/** One stuck task on a merged card: its own line, its own reason. */
export interface CardItem {
  taskId: string;
  /** Letter avatar (M / O / A; global cards S / $) — the only iconography a card carries (V0.9④). */
  avatar: string;
  label: string;
  reason?: string | null;
}

export interface RecoveryCard {
  id: string;
  shape: RecoveryCardShape;
  /** Present for observability; NEVER branch layout or copy on it — that is `shape`'s job. */
  disposition?: string | null;
  avatar: string;
  /** 「agent · 任务类型」 second line comes from this (V0.9③ — no breadcrumbs, no session names). */
  taskKindLabel?: string | null;
  headline: string;
  consequence?: string | null;
  items?: CardItem[] | null;
  affectedCount: number;
  actions: CardAction[];
  version: number;
  schemaVersion: number;
}

/** 「Reran N tasks after …」 — the green auto-handled line(s) above the cards. */
export interface RecoverySummaryLine {
  cause: string;
  count: number;
  text: string;
  at?: string | null;
}

export interface RecoveryPanel {
  cards: RecoveryCard[];
  count: number;
  summary: RecoverySummaryLine[];
}

/**
 * A press refused because the card moved (stale token, action no longer offered, or someone
 * else's press holds the lock). Carries the card as it is NOW when the server could assemble it —
 * repaint from it and stop; do not retry the press.
 */
export class CardActionConflict extends Error {
  readonly card: RecoveryCard | null;
  constructor(card: RecoveryCard | null, message: string) {
    super(message);
    this.name = 'CardActionConflict';
    this.card = card;
  }
}

const base = (userId: string) => `/digital-staff/store-manager/cards/${encodeURIComponent(userId)}`;

export async function getRecoveryPanel(userId: string): Promise<RecoveryPanel> {
  const response = await apiClient.chatbotGet<RecoveryPanel>(base(userId));
  const data = response.data as unknown;
  if (isErrorEnvelope(data)) throw new Error(data.errorMessage);
  const panel = data as Partial<RecoveryPanel> | null;
  return {
    cards: Array.isArray(panel?.cards) ? (panel!.cards as RecoveryCard[]) : [],
    count: panel?.count ?? 0,
    summary: Array.isArray(panel?.summary) ? (panel!.summary as RecoverySummaryLine[]) : [],
  };
}

/** The red-dot number. A separate, cheaper endpoint than the panel — drawn on every page. */
export async function getRecoveryBadgeCount(userId: string): Promise<number> {
  const response = await apiClient.chatbotGet<{ count: number }>(`${base(userId)}/badge`);
  const data = response.data as unknown;
  if (isErrorEnvelope(data)) return 0;
  return (data as { count?: number })?.count ?? 0;
}

/** 简报的「Anything to watch」：按 agent 一行，只给数字和入口（PRD #12 / UI 清单行17）。 */
export interface WatchLine {
  agent: string;
  count: number;
  /** 「Maya 3 items」——句子由服务端拼，前端不组装文案。 */
  text: string;
}

export interface AnythingToWatch {
  lines: WatchLine[];
  count: number;
  /** 只有一张卡时给它的 id，点进去直接定位；多于一张时为 null。 */
  focusCardId: string | null;
}

/**
 * 简报里那一块。
 *
 * <p>只读：它给的是数字和一个进面板的入口，不带任何恢复动作 —— 恢复只有面板一个地方，
 * 简报上再放一套按钮就成了两个地方能处理同一件事。
 *
 * <p>失败不抛给调用方：简报的其余部分比这一块重要得多，拿不到就当没有。
 */
export async function getAnythingToWatch(userId: string): Promise<AnythingToWatch> {
  const empty: AnythingToWatch = { lines: [], count: 0, focusCardId: null };
  try {
    const response = await apiClient.chatbotGet<AnythingToWatch>(`${base(userId)}/watch`);
    const data = response.data as unknown;
    if (isErrorEnvelope(data)) return empty;
    const d = data as Partial<AnythingToWatch> | null;
    return {
      lines: Array.isArray(d?.lines) ? (d!.lines as WatchLine[]) : [],
      count: d?.count ?? 0,
      focusCardId: d?.focusCardId ?? null,
    };
  } catch {
    return empty;
  }
}

/**
 * Press one button on one card.
 *
 * The token rides in the query string — the endpoint is a bodyless POST like every other button
 * in this data plane. Success returns the card as the press left it; a stale press throws
 * {@link CardActionConflict} with the current card. Either way the caller's next move is the
 * same: repaint from what came back (and re-pull the panel if the card is gone).
 */
export async function actOnCard(
  userId: string,
  cardId: string,
  actionId: string,
  token: string
): Promise<RecoveryCard> {
  const response = await apiClient.chatbotPost<RecoveryCard>(
    `${base(userId)}/${encodeURIComponent(cardId)}/actions/${encodeURIComponent(actionId)}`,
    undefined,
    { params: { token } }
  );
  const data = response.data as unknown;
  if (isErrorEnvelope(data)) {
    if (data.errorCode === 409) {
      throw new CardActionConflict((data.data as RecoveryCard) ?? null, data.errorMessage);
    }
    throw new Error(data.errorMessage);
  }
  return data as RecoveryCard;
}
